import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { money } from "@/lib/locale";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { Shield, Plus, Trash2, Phone, ChevronDown, Paperclip } from "lucide-react";
import ReceiptUploader from "@/components/ReceiptUploader";
import { toast } from "sonner";

const TYPES = ["Home", "Auto", "Health", "Contents", "Life", "Travel", "Other"];
const FREQUENCIES = [
  { value: "weekly", label: "per week" },
  { value: "fortnightly", label: "per fortnight" },
  { value: "monthly", label: "per month" },
  { value: "yearly", label: "per year" },
];
const FREQ_LABEL = Object.fromEntries(FREQUENCIES.map((f) => [f.value, f.label]));

export default function Insurance() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const blank = { policy_type: "Home", provider: "", policy_number: "", premium: "", premium_frequency: "yearly", renewal_date: "", contact_phone: "", notes: "" };
  const [form, setForm] = useState(blank);

  const load = useCallback(async () => { const { data } = await api.get("/insurance"); setItems(data); }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.provider.trim()) return toast.error("Provider is required");
    try {
      const payload = {
        ...form,
        premium: form.premium ? parseFloat(form.premium) : null,
        premium_frequency: form.premium_frequency || "yearly",
      };
      if (editId) await api.put(`/insurance/${editId}`, payload);
      else await api.post("/insurance", payload);
      setOpen(false); setForm(blank); setEditId(null); toast.success("Saved"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const startEdit = (it) => { setForm({ ...blank, ...it }); setEditId(it.id); setOpen(true); };
  const remove = async (id) => { await api.delete(`/insurance/${id}`); load(); };

  return (
    <div>
      <PageHeader
        eyebrow="Coverage"
        title="Insurance"
        subtitle="House, car, health and beyond — renewals at a glance."
        action={
          <Button data-testid="ins-add-button" onClick={() => { setEditId(null); setForm(blank); setOpen(true); }}
            className="rounded-full bg-hearth-terracotta hover:bg-hearth-terracottaDark text-white px-6 h-11">
            <Plus size={16} className="mr-1.5" /> Add policy
          </Button>
        }
      />

      {items.length === 0 ? (
        <EmptyState icon={Shield} title="No policies tracked." message="Add your home, car, health and contents policies so renewals never sneak up on you." />
      ) : (
        <div className="space-y-3">
          {items.map((it) => {
            const isExp = expanded === it.id;
            return (
              <div key={it.id} data-testid={`ins-row-${it.id}`} className="warm-card overflow-hidden fade-up">
                <button onClick={() => setExpanded(isExp ? null : it.id)} className="w-full p-6 flex items-center gap-4 text-left">
                  <div className="w-10 h-10 rounded-2xl bg-hearth-ink/8 text-hearth-ink grid place-items-center"><Shield size={18} strokeWidth={1.6} /></div>
                  <div className="flex-1 min-w-0">
                    <div className="caption text-hearth-mute">{it.policy_type}</div>
                    <div className="font-display text-2xl text-hearth-ink font-semibold truncate">{it.provider}</div>
                  </div>
                  <div className="text-right hidden sm:block">
                    <div className="caption text-hearth-mute">Renews</div>
                    <div className="text-hearth-ink font-medium">{it.renewal_date || "—"}</div>
                  </div>
                  {it.premium != null && it.premium > 0 && (
                    <div className="text-right hidden lg:block" data-testid={`ins-premium-summary-${it.id}`}>
                      <div className="caption text-hearth-mute">Premium</div>
                      <div className="text-hearth-ink font-medium font-mono">
                        {money(it.premium)}
                        <span className="text-hearth-mute text-xs ml-1">{FREQ_LABEL[it.premium_frequency] || FREQ_LABEL.yearly}</span>
                      </div>
                    </div>
                  )}
                  <span
                    data-testid={`ins-receipts-hint-${it.id}`}
                    title={(it.receipts?.length || 0) === 0 ? "Tap to attach a receipt" : `${it.receipts.length} receipt${it.receipts.length === 1 ? "" : "s"} attached`}
                    className={`hidden md:inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full ${
                      (it.receipts?.length || 0) > 0
                        ? "bg-hearth-sage/15 text-hearth-sageDark"
                        : "bg-hearth-stone text-hearth-mute"
                    }`}
                  >
                    <Paperclip size={11} />
                    {(it.receipts?.length || 0) > 0 ? it.receipts.length : "Add"}
                  </span>
                  <ChevronDown size={18} className={`text-hearth-mute transition-transform ${isExp ? "rotate-180" : ""}`} />
                </button>
                {isExp && (
                  <div className="px-6 pb-6 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm border-t border-hearth-line/70 pt-4">
                    <div><p className="caption text-hearth-mute">Policy #</p><p className="text-hearth-ink mt-1">{it.policy_number || "—"}</p></div>
                    <div>
                      <p className="caption text-hearth-mute">Premium</p>
                      <p className="text-hearth-ink mt-1" data-testid={`ins-premium-${it.id}`}>
                        {money(it.premium)}
                        {it.premium != null && it.premium > 0 && (
                          <span className="text-hearth-mute text-xs ml-1.5">{FREQ_LABEL[it.premium_frequency] || FREQ_LABEL.yearly}</span>
                        )}
                      </p>
                    </div>
                    <div><p className="caption text-hearth-mute">Phone</p><p className="text-hearth-ink mt-1">{it.contact_phone || "—"}</p></div>
                    <div><p className="caption text-hearth-mute">Renewal</p><p className="text-hearth-ink mt-1">{it.renewal_date || "—"}</p></div>
                    {it.notes && <div className="col-span-2 md:col-span-4"><p className="caption text-hearth-mute">Notes</p><p className="text-hearth-ink mt-1">{it.notes}</p></div>}
                    <div className="col-span-2 md:col-span-4 flex gap-2 pt-2">
                      {it.contact_phone && <a href={`tel:${it.contact_phone}`} className="pill-btn text-sm border border-hearth-line bg-hearth-paper hover:bg-hearth-line/50 inline-flex items-center gap-1"><Phone size={13} /> Call</a>}
                      <Button variant="ghost" data-testid={`ins-edit-${it.id}`} onClick={() => startEdit(it)} className="rounded-full text-sm">Edit</Button>
                      <Button variant="ghost" data-testid={`ins-delete-${it.id}`} onClick={() => remove(it.id)} className="rounded-full text-sm text-hearth-coral hover:bg-hearth-coral/10"><Trash2 size={14} className="mr-1" />Delete</Button>
                    </div>
                    <div className="col-span-2 md:col-span-4 pt-2 border-t border-hearth-line/70">
                      <p className="caption mb-2">Receipts / policy docs</p>
                      <ReceiptUploader kind="insurance" itemId={it.id} receipts={it.receipts || []} onChange={load} />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-3xl border-hearth-line bg-hearth-paper max-w-xl">
          <DialogHeader><DialogTitle className="font-display text-3xl">{editId ? "Edit policy" : "Add insurance"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="caption text-hearth-mute">Type</Label>
              <Select value={form.policy_type} onValueChange={(v) => setForm({ ...form, policy_type: v })}>
                <SelectTrigger className="input-warm mt-1.5 h-11 rounded-2xl" data-testid="ins-form-type"><SelectValue /></SelectTrigger>
                <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="caption text-hearth-mute">Provider</Label>
              <Input data-testid="ins-form-provider" className="input-warm mt-1.5 h-11" value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} />
            </div>
            <div>
              <Label className="caption text-hearth-mute">Policy number</Label>
              <Input className="input-warm mt-1.5 h-11" value={form.policy_number || ""} onChange={(e) => setForm({ ...form, policy_number: e.target.value })} />
            </div>
            <div>
              <Label className="caption text-hearth-mute">Premium</Label>
              <Input type="number" step="0.01" className="input-warm mt-1.5 h-11" data-testid="ins-form-premium" value={form.premium || ""} onChange={(e) => setForm({ ...form, premium: e.target.value })} placeholder="e.g. 120.00" />
            </div>
            <div>
              <Label className="caption text-hearth-mute">Billed</Label>
              <Select value={form.premium_frequency || "yearly"} onValueChange={(v) => setForm({ ...form, premium_frequency: v })}>
                <SelectTrigger className="input-warm mt-1.5 h-11 rounded-2xl" data-testid="ins-form-frequency"><SelectValue /></SelectTrigger>
                <SelectContent>{FREQUENCIES.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="caption text-hearth-mute">Renewal date</Label>
              <Input type="date" className="input-warm mt-1.5 h-11" value={form.renewal_date || ""} onChange={(e) => setForm({ ...form, renewal_date: e.target.value })} />
            </div>
            <div>
              <Label className="caption text-hearth-mute">Contact phone</Label>
              <Input className="input-warm mt-1.5 h-11" value={form.contact_phone || ""} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} placeholder="+61 1300 XXX XXX" />
            </div>
            <div className="md:col-span-2">
              <Label className="caption text-hearth-mute">Notes</Label>
              <Textarea className="input-warm mt-1.5" rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
            <Button data-testid="ins-form-save" onClick={save} className="rounded-full bg-hearth-terracotta hover:bg-hearth-terracottaDark text-white">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
